import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/Toast';
import axios from 'axios';
import QRCode from 'qrcode';
import imageCompression from 'browser-image-compression';
import {
  Shield,
  Plus,
  Search,
  Filter,
  Download,
  Printer,
  Eye,
  Edit,
  Trash2,
  ChevronDown,
  ChevronUp,
  X,
  Upload,
  FileText,
  Image as ImageIcon,
  Calendar,
  MapPin,
  Hash,
  Package,
  Wrench,
  Sparkles,
  Clock,
  AlertTriangle,
  CheckCircle,
  User,
  UserPlus,
  RotateCcw,
  QrCode,
  RefreshCw,
  BarChart3,
  History,
  ArrowUpDown,
  Loader2,
  ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import AmmoManagement from './AmmoManagement';
import RangeManagement from './RangeManagement';
import KeyAdmin from './KeyAdmin';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Norwegian translations (from no.json)
const t = {
  title: 'Klubb drift',
  description: 'Administrer klubbens drift og ressurser',
  modules: {
    weaponManagement: 'Våpenforvaltning',
    ammoManagement: 'Ammunisjonsforvaltning',
    rangeManagement: 'Baneforvaltning'
  },
  weapon: {
    title: 'Våpen',
    addNew: 'Legg til våpen',
    edit: 'Rediger våpen',
    delete: 'Slett våpen',
    brand: 'Merke',
    model: 'Modell',
    caliber: 'Kaliber',
    serialNumber: 'Serienummer',
    magazineCount: 'Antall magasiner',
    location: 'Lokasjon',
    status: 'Status',
    images: 'Bilder',
    documents: 'Dokumenter',
    weaponCard: 'Våpenkort',
    qrCode: 'QR-kode',
    downloadQR: 'Last ned QR-kode',
    printQR: 'Skriv ut QR-kode'
  },
  dashboard: {
    title: 'Oversikt',
    totalWeapons: 'Totalt antall våpen',
    byCaliberTitle: 'Våpen per kaliber',
    byLocationTitle: 'Våpen per lokasjon',
    maintenanceNeeded: 'Trenger vedlikehold',
    recentlyMaintained: 'Nylig vedlikeholdt',
    currentlyLoaned: 'Utlånt nå',
    mostCleaned: 'Mest pusset',
    leastCleaned: 'Minst pusset'
  },
  maintenance: {
    title: 'Vedlikehold',
    cleaning: 'Pussing',
    repair: 'Reparasjon',
    service: 'Service',
    log: 'Vedlikeholdslogg',
    history: 'Historikk',
    lastCleaned: 'Sist pusset',
    lastMaintained: 'Sist vedlikeholdt',
    cleanedBy: 'Pusset av',
    notes: 'Notater'
  },
  loan: {
    title: 'Utlån',
    loanOut: 'Lån ut',
    returnWeapon: 'Returner våpen',
    loanHistory: 'Utlånshistorikk',
    currentLoans: 'Aktive utlån',
    borrower: 'Låntaker',
    purpose: 'Bruksområde',
    fromDate: 'Fra dato',
    toDate: 'Til dato'
  },
  common: {
    save: 'Lagre',
    cancel: 'Avbryt',
    delete: 'Slett',
    edit: 'Rediger',
    add: 'Legg til',
    search: 'Søk',
    filter: 'Filtrer',
    loading: 'Laster...',
    noData: 'Ingen data',
    confirm: 'Bekreft',
    close: 'Lukk',
    actions: 'Handlinger',
    details: 'Detaljer'
  }
};

const ClubOperations = () => {
  const { user, isAdmin, isOrgAdmin, isRangeOfficer } = useAuth();
  const toast = useToast();

  // Main state
  const [activeModule, setActiveModule] = useState('weapons');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);

  // Weapons data
  const [weapons, setWeapons] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditExpanded, setAuditExpanded] = useState(false);
  const [weaponStats, setWeaponStats] = useState(null);
  const [locations, setLocations] = useState([]);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [caliberFilter, setCaliberFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Modals
  const [showAddWeaponModal, setShowAddWeaponModal] = useState(false);
  const [showWeaponDetailModal, setShowWeaponDetailModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedWeapon, setSelectedWeapon] = useState(null);

  // Form data
  const [weaponFormData, setWeaponFormData] = useState({
    brand: '',
    model: '',
    caliber: '',
    serial_number: '',
    magazine_count: 1,
    location_id: '',
    location_name: '',
    status: 'available',
    custom_status: '',
    notes: ''
  });

  // Weapon officer state
  const [weaponOfficer, setWeaponOfficer] = useState(null);
  const [weaponOfficerLoading, setWeaponOfficerLoading] = useState(false);
  const [reportFrequency, setReportFrequency] = useState(null);
  const [orgMembers, setOrgMembers] = useState([]);
  
  // Predefined status options
  const statusOptions = [
    { value: 'available', label: 'Tilgjengelig', color: 'green' },
    { value: 'loaned', label: 'Utlånt', color: 'orange' },
    { value: 'maintenance', label: 'Under vedlikehold', color: 'blue' },
    { value: 'repair', label: 'Til reparasjon', color: 'red' },
    { value: 'decommissioned', label: 'Utgått/Kassert', color: 'gray' },
    { value: 'reserved', label: 'Reservert', color: 'purple' },
    { value: 'custom', label: 'Egendefinert...', color: 'yellow' }
  ];
  const [isEditing, setIsEditing] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  // Image upload state
  const [weaponImages, setWeaponImages] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [showDeleteImageConfirm, setShowDeleteImageConfirm] = useState(null);
  const fileInputRef = useRef(null);

  // Custom location state
  const [showCustomLocation, setShowCustomLocation] = useState(false);
  const [customLocationName, setCustomLocationName] = useState('');

  // Sorting
  const [sortColumn, setSortColumn] = useState('brand');
  const [sortDirection, setSortDirection] = useState('asc');

  // History state
  const [weaponHistory, setWeaponHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilter, setHistoryFilter] = useState('all'); // all, cleaning, maintenance, loan
  const [historyLimit, setHistoryLimit] = useState(25); // 25, 50, 100, 'all'
  const [historyPage, setHistoryPage] = useState(1);
  const [editingHistoryItem, setEditingHistoryItem] = useState(null);
  const [editHistoryForm, setEditHistoryForm] = useState({ notes: '', description: '', cost: '' });

  // Active loans state
  const [activeLoans, setActiveLoans] = useState([]);

  // Quick loan / return state
  const [showQuickLoanModal, setShowQuickLoanModal] = useState(false);
  const [showQuickReturnModal, setShowQuickReturnModal] = useState(false);
  const [quickLoanWeapon, setQuickLoanWeapon] = useState(null);
  const [quickLoanForm, setQuickLoanForm] = useState({ borrower_id: '', borrower_name: '', expected_return_date: '', notes: '' });
  const [quickReturnForm, setQuickReturnForm] = useState({ returned_date: new Date().toISOString().split('T')[0], notes: '' });
  const [quickActionLoading, setQuickActionLoading] = useState(false);

  // Check access
  const hasAccess = isAdmin || isOrgAdmin || isRangeOfficer;

  // Load data on mount
  useEffect(() => {
    if (hasAccess && user?.organization_id) {
      loadWeapons();
      loadWeaponStats();
      loadLocations();
      loadActiveLoans();
      loadWeaponOfficer();
      loadWeaponAuditLogs();
      if (isAdmin || isOrgAdmin) {
        loadOrgMembers();
      }
    }
  }, [user?.organization_id, hasAccess]);

  const loadWeaponAuditLogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API_BASE}/api/club-operations/weapons/audit-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAuditLogs(r.data?.logs || []);
    } catch (e) {
      console.error('Failed to load weapon audit logs', e);
    }
  };

  // Load history when history tab is active
  useEffect(() => {
    if (activeTab === 'history' && hasAccess) {
      loadWeaponHistory();
    }
  }, [activeTab, historyFilter, historyLimit, hasAccess]);

  // Weapon Excel Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [importConfirming, setImportConfirming] = useState(false);

  const openImportModal = () => {
    setImportFile(null);
    setImportPreview(null);
    setShowImportModal(true);
  };

  const handleImportPreview = async () => {
    if (!importFile) {
      toast.error('Velg en Excel-fil først');
      return;
    }
    setImportLoading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', importFile);
      const res = await axios.post(`${API_BASE}/api/club-operations/weapons/import/preview`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setImportPreview(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke lese Excel-fil');
    } finally {
      setImportLoading(false);
    }
  };

  const handleImportConfirm = async () => {
    if (!importPreview) return;
    const importable = (importPreview.weapons || []).filter(
      w => !w.is_duplicate_in_db && !w.is_duplicate_in_file && (!w.errors || w.errors.length === 0)
    );
    if (importable.length === 0) {
      toast.error('Ingen gyldige våpen å importere');
      return;
    }
    setImportConfirming(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE}/api/club-operations/weapons/import/confirm`, { weapons: importable }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Importerte ${res.data.inserted} våpen`);
      setShowImportModal(false);
      setImportFile(null);
      setImportPreview(null);
      await Promise.all([loadWeapons(), loadWeaponStats()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import feilet');
    } finally {
      setImportConfirming(false);
    }
  };

  const loadWeapons = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/club-operations/weapons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeapons(response.data || []);
    } catch (error) {
      console.error('Error loading weapons:', error);
      toast.error('Kunne ikke laste våpen');
    } finally {
      setLoading(false);
    }
  };

  const loadWeaponStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/club-operations/weapons/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeaponStats(response.data);
    } catch (error) {
      console.error('Error loading weapon stats:', error);
    }
  };

  const loadLocations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/organizations/${user.organization_id}/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data || []);
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  // Load weapon officer
  const loadWeaponOfficer = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/club-operations/weapons/weapon-officer`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeaponOfficer(response.data.weapon_officer);
      setReportFrequency(response.data.report_frequency);
    } catch (error) {
      console.error('Error loading weapon officer:', error);
    }
  };

  // Load org members for dropdown
  const loadOrgMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrgMembers(response.data || []);
    } catch (error) {
      console.error('Error loading members:', error);
    }
  };

  // Open quick loan modal for a weapon
  const openQuickLoan = (weapon) => {
    if (orgMembers.length === 0) loadOrgMembers();
    setQuickLoanWeapon(weapon);
    setQuickLoanForm({ borrower_id: '', borrower_name: '', expected_return_date: '', notes: '' });
    setShowQuickLoanModal(true);
  };

  // Submit quick loan
  const submitQuickLoan = async () => {
    if (!quickLoanWeapon) return;
    if (!quickLoanForm.borrower_id && !quickLoanForm.borrower_name.trim()) {
      toast.error('Velg medlem eller skriv inn navn');
      return;
    }
    setQuickActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE}/api/club-operations/weapons/${quickLoanWeapon.id}/quick-loan`,
        {
          borrower_id: quickLoanForm.borrower_id || '',
          borrower_name: quickLoanForm.borrower_name.trim(),
          expected_return_date: quickLoanForm.expected_return_date || '',
          notes: quickLoanForm.notes.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Våpen registrert som utlånt');
      setShowQuickLoanModal(false);
      setQuickLoanWeapon(null);
      await Promise.all([loadWeapons(), loadWeaponStats(), loadActiveLoans()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke registrere utlån');
    } finally {
      setQuickActionLoading(false);
    }
  };

  // Open quick return modal
  const openQuickReturn = (weapon) => {
    setQuickLoanWeapon(weapon);
    setQuickReturnForm({ returned_date: new Date().toISOString().split('T')[0], notes: '' });
    setShowQuickReturnModal(true);
  };

  // Submit quick return
  const submitQuickReturn = async () => {
    if (!quickLoanWeapon) return;
    setQuickActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE}/api/club-operations/weapons/${quickLoanWeapon.id}/quick-return`,
        {
          returned_date: quickReturnForm.returned_date,
          notes: quickReturnForm.notes.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Våpen markert som tilbakelevert');
      setShowQuickReturnModal(false);
      setQuickLoanWeapon(null);
      await Promise.all([loadWeapons(), loadWeaponStats(), loadActiveLoans()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke registrere innlevering');
    } finally {
      setQuickActionLoading(false);
    }
  };

  // Set weapon officer
  const handleSetWeaponOfficer = async (userId) => {
    setWeaponOfficerLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/api/club-operations/weapons/weapon-officer`,
        { user_id: userId },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      toast.success('Våpenansvarlig oppdatert');
      loadWeaponOfficer();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke sette våpenansvarlig');
    } finally {
      setWeaponOfficerLoading(false);
    }
  };

  // Set report frequency
  const handleSetReportFrequency = async (frequency) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/api/club-operations/weapons/weapon-officer/report-settings`,
        { frequency },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      setReportFrequency(frequency);
      toast.success('Rapportinnstillinger oppdatert');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke oppdatere rapportinnstillinger');
    }
  };

  const loadWeaponHistory = async () => {
    try {
      setHistoryLoading(true);
      setHistoryPage(1); // Reset page when loading new data
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      // Use a high limit and handle pagination client-side for better UX
      params.append('limit', historyLimit === 'all' ? '1000' : '1000');
      if (historyFilter !== 'all') {
        params.append('history_type', historyFilter);
      }
      const response = await axios.get(`${API_BASE}/api/club-operations/weapons/history?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWeaponHistory(response.data || []);
    } catch (error) {
      console.error('Error loading weapon history:', error);
      toast.error('Kunne ikke laste historikk');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Load active loans
  const loadActiveLoans = async () => {
    try {
      const token = localStorage.getItem('token');
      // Get all loans and filter to active ones
      const response = await axios.get(`${API_BASE}/api/club-operations/weapons/loans/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActiveLoans(response.data || []);
    } catch (error) {
      console.error('Error loading active loans:', error);
      // Fallback: filter from weapons list - check both is_loaned and status
      const loaned = weapons.filter(w => w.is_loaned || w.status === 'loaned');
      setActiveLoans(loaned.map(w => ({
        id: w.current_loan_id || w.id,
        weapon_names: [`${w.brand} ${w.model}`],
        weapon_caliber: w.caliber,
        borrower_name: w.loaned_to || 'Ukjent',
        loan_from_date: null,
        loan_to_date: null
      })));
    }
  };

  // Get unique calibers from weapons
  const uniqueCalibers = useMemo(() => {
    const calibers = new Set(weapons.map(w => w.caliber).filter(Boolean));
    return Array.from(calibers).sort();
  }, [weapons]);

  // Filtered and sorted weapons
  const filteredWeapons = useMemo(() => {
    let result = [...weapons];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(w =>
        w.brand?.toLowerCase().includes(query) ||
        w.model?.toLowerCase().includes(query) ||
        w.caliber?.toLowerCase().includes(query) ||
        w.serial_number?.toLowerCase().includes(query)
      );
    }

    // Caliber filter
    if (caliberFilter !== 'all') {
      result = result.filter(w => w.caliber === caliberFilter);
    }

    // Location filter
    if (locationFilter !== 'all') {
      result = result.filter(w => w.location_id === locationFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'loaned') {
        result = result.filter(w => w.is_loaned);
      } else if (statusFilter === 'available') {
        result = result.filter(w => !w.is_loaned);
      }
    }

    // Sorting
    result.sort((a, b) => {
      let aVal = a[sortColumn] || '';
      let bVal = b[sortColumn] || '';
      
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [weapons, searchQuery, caliberFilter, locationFilter, statusFilter, sortColumn, sortDirection]);

  // Helper: Get cleaning status color based on last_cleaned date
  const getCleaningColor = (lastCleaned) => {
    if (!lastCleaned) return '#ef4444'; // Red for never cleaned
    const now = new Date();
    const cleaned = new Date(lastCleaned);
    const daysSince = Math.floor((now - cleaned) / (1000 * 60 * 60 * 24));
    if (daysSince < 30) return '#22c55e'; // Green
    if (daysSince < 90) return '#eab308'; // Yellow
    return '#ef4444'; // Red
  };

  // Report frequency labels
  const frequencyLabels = {
    daily: 'Daglig',
    weekly: 'Ukentlig',
    monthly: 'Månedlig',
    quarterly: 'Kvartalsvis',
    semi_annual: 'Halvårlig',
    annual: 'Årlig'
  };

  // Handle sort
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Reset form
  const resetForm = () => {
    setWeaponFormData({
      brand: '',
      model: '',
      caliber: '',
      serial_number: '',
      magazine_count: 1,
      location_id: '',
      location_name: '',
      status: 'available',
      custom_status: '',
      notes: ''
    });
    setIsEditing(false);
    setWeaponImages([]);
    setShowCustomLocation(false);
    setCustomLocationName('');
  };

  // Open add modal
  const handleAddWeapon = () => {
    resetForm();
    setShowAddWeaponModal(true);
  };

  // Open edit modal
  const handleEditWeapon = (weapon) => {
    // Determine if status is custom (not in predefined list)
    const predefinedStatuses = ['available', 'loaned', 'maintenance', 'repair', 'decommissioned', 'reserved'];
    const weaponStatus = weapon.status || (weapon.is_loaned ? 'loaned' : 'available');
    const isCustomStatus = weaponStatus && !predefinedStatuses.includes(weaponStatus);
    
    setWeaponFormData({
      brand: weapon.brand || '',
      model: weapon.model || '',
      caliber: weapon.caliber || '',
      serial_number: weapon.serial_number || '',
      magazine_count: weapon.magazine_count || 1,
      location_id: weapon.location_id || '',
      location_name: weapon.location_name || '',
      status: isCustomStatus ? 'custom' : weaponStatus,
      custom_status: isCustomStatus ? weaponStatus : '',
      notes: weapon.notes || ''
    });
    setSelectedWeapon(weapon);
    setIsEditing(true);
    setWeaponImages(weapon.images || []);
    // Check if location is custom (not in locations list)
    const isCustomLoc = weapon.location_id === 'custom' || 
      (weapon.location_name && !locations.find(l => l.id === weapon.location_id));
    setShowCustomLocation(isCustomLoc);
    setCustomLocationName(isCustomLoc ? weapon.location_name : '');
    setShowAddWeaponModal(true);
  };

  // Image compression function
  const compressImage = async (file) => {
    const options = {
      maxSizeMB: 1.5,
      maxWidthOrHeight: 1200,
      useWebWorker: true,
      fileType: 'image/jpeg',
      initialQuality: 0.75,
      onProgress: (progress) => {
        setCompressionProgress(Math.round(progress));
      }
    };

    try {
      const compressedFile = await imageCompression(file, options);
      const originalName = file.name;
      const nameWithoutExt = originalName.substring(0, originalName.lastIndexOf('.')) || originalName;
      const renamedFile = new File([compressedFile], `${nameWithoutExt}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now()
      });
      setCompressionProgress(null);
      return renamedFile;
    } catch (error) {
      console.error('Compression failed:', error);
      setCompressionProgress(null);
      return file;
    }
  };

  // Handle image upload
  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploadingImage(true);
    const newImages = [];
    
    try {
      for (const file of files) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          toast.error(`${file.name} er ikke et bilde`);
          continue;
        }
        
        // Validate file size (max 10MB before compression)
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} er for stor. Maks 10MB.`);
          continue;
        }
        
        // Compress image
        console.log(`Compressing ${file.name}...`);
        const compressedFile = await compressImage(file);
        console.log(`Compressed from ${(file.size / 1024 / 1024).toFixed(2)}MB to ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB`);
        
        // If editing existing weapon, upload immediately
        if (isEditing && selectedWeapon) {
          const formData = new FormData();
          formData.append('file', compressedFile);
          
          const token = localStorage.getItem('token');
          const response = await axios.post(
            `${API_BASE}/api/club-operations/weapons/${selectedWeapon.id}/upload-image`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
              }
            }
          );
          
          newImages.push(response.data.url);
        } else {
          // For new weapons, store locally until save
          const reader = new FileReader();
          const dataUrl = await new Promise((resolve) => {
            reader.onload = (e) => resolve(e.target.result);
            reader.readAsDataURL(compressedFile);
          });
          newImages.push({ dataUrl, file: compressedFile, isLocal: true });
        }
      }
      
      setWeaponImages(prev => [...prev, ...newImages]);
      toast.success(`${newImages.length} bilde(r) lastet opp`);
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Kunne ikke laste opp bilde(r)');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle image delete
  const handleDeleteImage = async (imageUrl, index) => {
    if (isEditing && selectedWeapon && typeof imageUrl === 'string') {
      // Delete from server
      try {
        const token = localStorage.getItem('token');
        await axios.delete(
          `${API_BASE}/api/club-operations/weapons/${selectedWeapon.id}/image`,
          {
            params: { image_url: imageUrl },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        toast.success('Bilde slettet');
      } catch (error) {
        console.error('Delete error:', error);
        toast.error('Kunne ikke slette bilde');
        return;
      }
    }
    
    setWeaponImages(prev => prev.filter((_, i) => i !== index));
    setShowDeleteImageConfirm(null);
  };

  // View weapon details
  const handleViewWeapon = (weapon) => {
    setSelectedWeapon(weapon);
    setShowWeaponDetailModal(true);
  };

  // Submit weapon form
  const handleSubmitWeapon = async (e) => {
    e.preventDefault();
    
    // Validate custom location if selected
    if (showCustomLocation && !customLocationName.trim()) {
      toast.error('Vennligst fyll ut egendefinert lokasjon');
      return;
    }
    
    // Validate custom status if selected
    if (weaponFormData.status === 'custom' && !weaponFormData.custom_status.trim()) {
      toast.error('Vennligst fyll ut egendefinert status');
      return;
    }
    
    setFormLoading(true);

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Prepare form data with custom location if needed
      const submitData = { ...weaponFormData };
      if (showCustomLocation && customLocationName) {
        submitData.location_id = 'custom';
        submitData.location_name = customLocationName;
      } else if (submitData.location_id && submitData.location_id !== 'custom') {
        const loc = locations.find(l => l.id === submitData.location_id);
        submitData.location_name = loc?.name || '';
      }
      
      // Handle status - use custom_status if 'custom' is selected
      if (submitData.status === 'custom') {
        submitData.status = submitData.custom_status;
      }
      // Remove custom_status from submit data (it's only for the form)
      delete submitData.custom_status;
      
      // Update is_loaned based on status
      submitData.is_loaned = submitData.status === 'loaned';

      console.log('Submitting weapon data:', submitData);

      if (isEditing && selectedWeapon) {
        // Update existing weapon
        await axios.put(
          `${API_BASE}/api/club-operations/weapons/${selectedWeapon.id}`,
          submitData,
          { headers }
        );
        toast.success('Våpen oppdatert');
      } else {
        // Create new weapon
        const response = await axios.post(
          `${API_BASE}/api/club-operations/weapons`,
          submitData,
          { headers }
        );
        
        console.log('Weapon created:', response.data);
        
        // Upload local images for new weapon
        const newWeaponId = response.data.id;
        const localImages = weaponImages.filter(img => img.isLocal);
        
        for (const img of localImages) {
          const formData = new FormData();
          formData.append('file', img.file);
          
          await axios.post(
            `${API_BASE}/api/club-operations/weapons/${newWeaponId}/upload-image`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
              }
            }
          );
        }
        
        toast.success('Våpen lagt til');
      }

      // Close modal and reset
      setShowAddWeaponModal(false);
      resetForm();
      
      // Reload data
      await loadWeapons();
      await loadWeaponStats();
      
    } catch (error) {
      console.error('Error saving weapon:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke lagre våpen');
    } finally {
      setFormLoading(false);
    }
  };

  // Delete weapon
  const handleDeleteWeapon = async () => {
    if (!selectedWeapon) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE}/api/club-operations/weapons/${selectedWeapon.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Våpen slettet');
      setShowDeleteConfirmModal(false);
      setSelectedWeapon(null);
      loadWeapons();
      loadWeaponStats();
    } catch (error) {
      console.error('Error deleting weapon:', error);
      toast.error('Kunne ikke slette våpen');
    }
  };

  // Handle editing history item
  const handleEditHistoryItem = (item) => {
    setEditingHistoryItem(item);
    setEditHistoryForm({
      notes: item.notes || '',
      description: item.description || '',
      cost: item.cost || ''
    });
  };

  // Handle save history item
  const handleSaveHistoryItem = async () => {
    if (!editingHistoryItem) return;
    
    try {
      const token = localStorage.getItem('token');
      const endpoint = editingHistoryItem.type === 'cleaning' 
        ? `/api/club-operations/weapons/cleaning/${editingHistoryItem.id}`
        : `/api/club-operations/weapons/maintenance/${editingHistoryItem.id}`;
      
      const payload = editingHistoryItem.type === 'cleaning'
        ? { notes: editHistoryForm.notes }
        : { description: editHistoryForm.description, cost: editHistoryForm.cost ? parseFloat(editHistoryForm.cost) : null };
      
      await axios.put(`${API_BASE}${endpoint}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Hendelse oppdatert');
      setEditingHistoryItem(null);
      loadWeaponHistory();
    } catch (error) {
      console.error('Error updating history item:', error);
      toast.error('Kunne ikke oppdatere hendelse');
    }
  };

  // Handle delete history item
  const handleDeleteHistoryItem = async (item) => {
    if (!confirm(`Er du sikker på at du vil slette denne ${item.type === 'cleaning' ? 'pussing' : 'vedlikehold'}s-hendelsen?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const endpoint = item.type === 'cleaning'
        ? `/api/club-operations/weapons/cleaning/${item.id}`
        : `/api/club-operations/weapons/maintenance/${item.id}`;
      
      await axios.delete(`${API_BASE}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Hendelse slettet');
      loadWeaponHistory();
    } catch (error) {
      console.error('Error deleting history item:', error);
      toast.error('Kunne ikke slette hendelse');
    }
  };

  // Generate and download QR code with weapon info
  const handleDownloadQR = async (weapon) => {
    try {
      // Use the weapon's QR code from database (format: AKT-WPN-XXXXXXXX)
      const qrData = weapon.qr_code;

      // Generate QR code as data URL
      const qrDataUrl = await QRCode.toDataURL(qrData, {
        width: 300,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });

      // Create canvas to combine QR code with text
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas size - calculate width based on longest text
      const qrSize = 300;
      const textPadding = 40;
      const textHeight = 200;
      const sidePadding = 80; // Extra padding on each side
      
      // Measure text widths to determine canvas size
      ctx.font = 'bold 45px Arial, sans-serif';
      const brandModelText = `${weapon.brand} ${weapon.model}`;
      const brandModelWidth = ctx.measureText(brandModelText).width;
      
      ctx.font = '40px Arial, sans-serif';
      const caliberText = `Kaliber: ${weapon.caliber}`;
      const caliberWidth = ctx.measureText(caliberText).width;
      
      ctx.font = 'bold 40px Arial, sans-serif';
      const serialText = `S/N: ${weapon.serial_number}`;
      const serialWidth = ctx.measureText(serialText).width;
      
      // Find the maximum width needed
      const maxTextWidth = Math.max(brandModelWidth, caliberWidth, serialWidth);
      const minCanvasWidth = Math.max(qrSize + sidePadding * 2, maxTextWidth + sidePadding * 2);
      
      canvas.width = minCanvasWidth;
      canvas.height = qrSize + textHeight + textPadding;
      
      // Fill white background
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Load and draw QR code
      const qrImage = new Image();
      qrImage.onload = () => {
        // Draw QR code centered horizontally
        const qrX = (canvas.width - qrSize) / 2;
        ctx.drawImage(qrImage, qrX, 20, qrSize, qrSize);
        
        // Add weapon info text below QR code - 250% larger fonts
        ctx.fillStyle = '#000000';
        ctx.textAlign = 'center';
        
        // Brand and Model (larger, bold)
        ctx.font = 'bold 45px Arial, sans-serif';
        ctx.fillText(brandModelText, canvas.width / 2, qrSize + 70);
        
        // Caliber
        ctx.font = '40px Arial, sans-serif';
        ctx.fillText(caliberText, canvas.width / 2, qrSize + 125);
        
        // Serial number - BLACK and bold
        ctx.font = 'bold 40px Arial, sans-serif';
        ctx.fillStyle = '#000000';
        ctx.fillText(serialText, canvas.width / 2, qrSize + 180);
        
        // Create download link
        const link = document.createElement('a');
        link.download = `QR-${weapon.brand}-${weapon.model}-${weapon.serial_number}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        
        toast.success('QR-kode lastet ned');
      };
      qrImage.src = qrDataUrl;

    } catch (error) {
      console.error('Error generating QR:', error);
      toast.error('Kunne ikke generere QR-kode');
    }
  };

  // Download weapons PDF report
  const handleDownloadWeaponsPDF = async () => {
    try {
      toast.info('Genererer PDF...');
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/api/club-operations/weapons/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'Vapenliste.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) {
          filename = match[1].replace(/['"]/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF lastet ned');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Kunne ikke generere PDF');
    }
  };

  // Get location name by ID
  const getLocationName = (locationId, weapon = null) => {
    // If weapon has a custom location_name, use that
    if (weapon?.location_name && (locationId === 'custom' || !locations.find(l => l.id === locationId))) {
      return weapon.location_name;
    }
    const location = locations.find(l => l.id === locationId);
    return location?.name || weapon?.location_name || 'Ukjent';
  };

  // Access denied view
  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-gray-400">
        <Shield className="w-16 h-16 mb-4 text-gray-600" />
        <h2 className="text-xl font-semibold mb-2">Ingen tilgang</h2>
        <p>Du har ikke tilgang til denne modulen.</p>
        <p className="text-sm mt-2">Kun skyteledere og administratorer har tilgang.</p>
      </div>
    );
  }

  // Module not enabled
  if (user?.organization?.club_operations_enabled !== true) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-gray-400">
        <Shield className="w-16 h-16 mb-4 text-gray-600" />
        <h2 className="text-xl font-semibold mb-2">Modul ikke aktivert</h2>
        <p>Klubb drift-modulen er ikke aktivert for din organisasjon.</p>
        <p className="text-sm mt-2">Kontakt en administrator for å aktivere denne funksjonen.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="club-operations-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            <Shield className="w-8 h-8 text-yellow-500" />
            {t.title}
          </h1>
          <p className="text-gray-400 mt-1">{t.description}</p>
        </div>
      </div>

      {/* Module Navigation */}
      <div className="mb-4">
        <p className="text-gray-400 text-sm mb-2">Forvaltning av:</p>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveModule('weapons')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeModule === 'weapons'
                ? 'bg-yellow-500 text-gray-900'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
            data-testid="module-weapons-btn"
          >
            <Shield className="w-4 h-4" />
            Våpen
          </button>
          <button
            onClick={() => setActiveModule('ammo')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeModule === 'ammo'
                ? 'bg-yellow-500 text-gray-900'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
            data-testid="module-ammo-btn"
          >
            <Package className="w-4 h-4" />
            Ammunisjon
          </button>
          <button
            onClick={() => setActiveModule('range')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeModule === 'range'
                ? 'bg-yellow-500 text-gray-900'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
            data-testid="module-range-btn"
          >
            <MapPin className="w-4 h-4" />
            Bane
          </button>
          <button
            onClick={() => setActiveModule('keys')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeModule === 'keys'
                ? 'bg-yellow-500 text-gray-900'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
            data-testid="module-keys-btn"
          >
            <Shield className="w-4 h-4" />
            Nøkkel-admin
          </button>
          <button
            onClick={() => window.location.assign('/merkeskyting')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors bg-gray-800 text-gray-300 hover:bg-gray-700"
            data-testid="module-merkeskyting-btn"
            title="Åpne Merkeskyting"
          >
            <ExternalLink className="w-4 h-4" />
            Merkeskyting
          </button>
        </div>
      </div>

      {/* Weapons Module */}
      {activeModule === 'weapons' && (
        <div className="space-y-6">
          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="bg-gray-800 border border-gray-700 flex-wrap h-auto">
              <TabsTrigger
                value="dashboard"
                className="data-[state=active]:bg-yellow-500 data-[state=active]:text-gray-900"
                data-testid="tab-dashboard"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                {t.dashboard.title}
              </TabsTrigger>
              <TabsTrigger
                value="list"
                className="data-[state=active]:bg-yellow-500 data-[state=active]:text-gray-900"
                data-testid="tab-list"
              >
                <Shield className="w-4 h-4 mr-2" />
                {t.weapon.title}
              </TabsTrigger>
              <TabsTrigger
                value="history"
                className="data-[state=active]:bg-yellow-500 data-[state=active]:text-gray-900"
                data-testid="tab-history"
              >
                <History className="w-4 h-4 mr-2" />
                {t.maintenance.history}
              </TabsTrigger>
            </TabsList>

            {/* Dashboard Tab */}
            <TabsContent value="dashboard" className="mt-6">
              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {/* Total weapons */}
                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-400">{t.dashboard.totalWeapons}</p>
                        <p className="text-3xl font-bold text-white">
                          {weaponStats?.total_weapons || 0}
                        </p>
                      </div>
                      <Shield className="w-10 h-10 text-yellow-500 opacity-50" />
                    </div>
                  </CardContent>
                </Card>

                {/* Currently loaned */}
                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-400">{t.dashboard.currentlyLoaned}</p>
                        <p className="text-3xl font-bold text-orange-400">
                          {weaponStats?.currently_loaned || 0}
                        </p>
                      </div>
                      <User className="w-10 h-10 text-orange-500 opacity-50" />
                    </div>
                  </CardContent>
                </Card>

                {/* Unique calibers */}
                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-400">Antall kalibre</p>
                        <p className="text-3xl font-bold text-blue-400">
                          {weaponStats?.by_caliber ? Object.keys(weaponStats.by_caliber).length : 0}
                        </p>
                      </div>
                      <Hash className="w-10 h-10 text-blue-500 opacity-50" />
                    </div>
                  </CardContent>
                </Card>

                {/* Unique locations */}
                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-400">Lokasjoner</p>
                        <p className="text-3xl font-bold text-green-400">
                          {weaponStats?.by_location ? Object.keys(weaponStats.by_location).filter(k => k !== 'null').length : 0}
                        </p>
                      </div>
                      <MapPin className="w-10 h-10 text-green-500 opacity-50" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* QR Scanner Call-to-Action */}
              <div className="mb-6">
                <a
                  href="/weapon-scanner"
                  className="flex items-center justify-center gap-4 w-full p-6 bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-400 hover:to-yellow-500 rounded-2xl text-gray-900 font-bold text-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-[1.02]"
                  data-testid="scan-weapon-qr-btn"
                >
                  <QrCode className="w-10 h-10" />
                  <span>Skann QR for å registrere pussing, vedlikehold, utlån</span>
                </a>
              </div>

              {/* Weapon Officer Section */}
              {(isAdmin || isOrgAdmin) && (
                <Card className="bg-gray-800 border-gray-700 mb-6" data-testid="weapon-officer-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-white flex items-center gap-2">
                      <User className="w-5 h-5 text-yellow-500" />
                      Våpenansvarlig
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                      Utnevn et medlem som våpenansvarlig og konfigurer rapportinnstillinger
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Select weapon officer */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1.5">Velg våpenansvarlig</label>
                        <Select
                          value={weaponOfficer?.id || ''}
                          onValueChange={(val) => handleSetWeaponOfficer(val)}
                          disabled={weaponOfficerLoading}
                        >
                          <SelectTrigger className="w-full bg-gray-700 border-gray-600 text-white" data-testid="weapon-officer-select">
                            <SelectValue placeholder="Velg et medlem...">
                              {weaponOfficer ? weaponOfficer.full_name : 'Velg et medlem...'}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-700 max-h-60">
                            {orgMembers.map(m => (
                              <SelectItem key={m.id} value={m.id} className="text-white">
                                {m.full_name} ({m.email})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {weaponOfficer && (
                          <p className="text-xs text-gray-500 mt-1.5">
                            E-post: {weaponOfficer.email}
                          </p>
                        )}
                      </div>
                      {/* Report frequency */}
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1.5">Rapportfrekvens</label>
                        <Select
                          value={reportFrequency || ''}
                          onValueChange={(val) => handleSetReportFrequency(val)}
                          disabled={!weaponOfficer}
                        >
                          <SelectTrigger className="w-full bg-gray-700 border-gray-600 text-white" data-testid="report-frequency-select">
                            <SelectValue placeholder="Velg frekvens...">
                              {reportFrequency ? frequencyLabels[reportFrequency] : 'Velg frekvens...'}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="bg-gray-800 border-gray-700">
                            {Object.entries(frequencyLabels).map(([val, label]) => (
                              <SelectItem key={val} value={val} className="text-white">{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {!weaponOfficer && (
                          <p className="text-xs text-gray-500 mt-1.5">Velg våpenansvarlig først</p>
                        )}
                        {weaponOfficer && reportFrequency && (
                          <p className="text-xs text-green-400 mt-1.5">
                            {frequencyLabels[reportFrequency]} rapport sendes til {weaponOfficer.email}
                          </p>
                        )}
                      </div>
                    </div>
                    {/* Color legend */}
                    <div className="mt-4 pt-3 border-t border-gray-700">
                      <p className="text-xs text-gray-400 mb-2">Fargekoder for "Sist pusset":</p>
                      <div className="flex flex-wrap gap-4">
                        <span className="flex items-center gap-1.5 text-xs text-gray-300">
                          <span className="inline-block w-2.5 h-2.5 rounded-full bg-green-500" /> Under 1 måned
                        </span>
                        <span className="flex items-center gap-1.5 text-xs text-gray-300">
                          <span className="inline-block w-2.5 h-2.5 rounded-full bg-yellow-500" /> 1-3 måneder
                        </span>
                        <span className="flex items-center gap-1.5 text-xs text-gray-300">
                          <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500" /> Over 3 måneder / Aldri
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Breakdown cards */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* By caliber */}
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Hash className="w-5 h-5 text-yellow-500" />
                      {t.dashboard.byCaliberTitle}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {weaponStats?.by_caliber && Object.keys(weaponStats.by_caliber).length > 0 ? (
                      <div className="space-y-3">
                        {Object.entries(weaponStats.by_caliber)
                          .sort((a, b) => b[1] - a[1])
                          .map(([caliber, count]) => (
                            <div key={caliber} className="flex items-center justify-between">
                              <span className="text-gray-300">{caliber}</span>
                              <div className="flex items-center gap-3">
                                <div className="w-32 bg-gray-700 rounded-full h-2">
                                  <div
                                    className="bg-yellow-500 h-2 rounded-full"
                                    style={{
                                      width: `${(count / weaponStats.total_weapons) * 100}%`
                                    }}
                                  />
                                </div>
                                <span className="text-white font-medium w-8 text-right">{count}</span>
                              </div>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">{t.common.noData}</p>
                    )}
                  </CardContent>
                </Card>

                {/* By location */}
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <MapPin className="w-5 h-5 text-yellow-500" />
                      {t.dashboard.byLocationTitle}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {weaponStats?.by_location && Object.keys(weaponStats.by_location).length > 0 ? (
                      <div className="space-y-3">
                        {Object.entries(weaponStats.by_location)
                          .sort((a, b) => b[1] - a[1])
                          .map(([locationName, count]) => (
                            <div key={locationName} className="flex items-center justify-between">
                              <span className="text-gray-300">{locationName}</span>
                              <div className="flex items-center gap-3">
                                <div className="w-32 bg-gray-700 rounded-full h-2">
                                  <div
                                    className="bg-green-500 h-2 rounded-full"
                                    style={{
                                      width: `${(count / weaponStats.total_weapons) * 100}%`
                                    }}
                                  />
                                </div>
                                <span className="text-white font-medium w-8 text-right">{count}</span>
                              </div>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">{t.common.noData}</p>
                    )}
                  </CardContent>
                </Card>

                {/* Most cleaned */}
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-yellow-500" />
                      {t.dashboard.mostCleaned}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {weaponStats?.most_cleaned && weaponStats.most_cleaned.length > 0 ? (
                      <div className="space-y-2">
                        {weaponStats.most_cleaned.map((item, idx) => (
                          <div key={idx} className="py-2 border-b border-gray-700 last:border-0">
                            <div className="flex items-center justify-between">
                              <span className="text-gray-300">
                                {item.name}
                              </span>
                              <span className="text-green-400 font-medium">{item.cleaning_count}x</span>
                            </div>
                            {item.last_cleaned_by && (
                              <div className="text-xs text-gray-500 mt-1">
                                Sist: {item.last_cleaned_by} - {item.last_cleaned_at ? new Date(item.last_cleaned_at).toLocaleDateString('nb-NO', {
                                  day: 'numeric',
                                  month: 'short',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }) : ''}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">{t.common.noData}</p>
                    )}
                  </CardContent>
                </Card>

                {/* Least cleaned */}
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-orange-500" />
                      {t.dashboard.leastCleaned}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {weaponStats?.least_cleaned && weaponStats.least_cleaned.length > 0 ? (
                      <div className="space-y-2">
                        {weaponStats.least_cleaned.map((item, idx) => (
                          <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-700 last:border-0">
                            <span className="text-gray-300">
                              {item.name}
                            </span>
                            <span className="text-orange-400 font-medium">{item.cleaning_count}x</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">{t.common.noData}</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Active Loans Section */}
              {weaponStats?.currently_loaned > 0 && (
                <Card className="bg-gray-800 border-gray-700 mt-6">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <User className="w-5 h-5 text-purple-500" />
                      Aktive utlån ({weaponStats?.currently_loaned || 0})
                    </CardTitle>
                    <CardDescription className="text-gray-400">
                      Våpen som er utlånt til medlemmer
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {activeLoans.length > 0 ? (
                      <div className="space-y-3">
                        {activeLoans.map((loan) => (
                          loan.loan_type === 'protocol' ? (
                            <a
                              key={loan.id}
                              href={`/weapon-loan/${loan.id}`}
                              className="flex items-center justify-between p-3 bg-gray-700/50 rounded-lg hover:bg-gray-700 transition-colors"
                            >
                              <div>
                                <p className="text-white font-medium">
                                  {loan.weapon_names?.join(', ') || 'Ukjent våpen'}
                                </p>
                                <p className="text-gray-400 text-sm">
                                  Utlånt til: {loan.borrower_name}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-purple-400 text-sm">
                                  {loan.loan_from_date ? new Date(loan.loan_from_date).toLocaleDateString('nb-NO', {
                                    day: 'numeric',
                                    month: 'short'
                                  }) : ''} - {loan.loan_to_date ? new Date(loan.loan_to_date).toLocaleDateString('nb-NO', {
                                    day: 'numeric',
                                    month: 'short'
                                  }) : ''}
                                </p>
                                <p className="text-xs text-gray-500">Klikk for detaljer</p>
                              </div>
                            </a>
                          ) : (
                            <div
                              key={loan.id}
                              className="flex items-center justify-between p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg"
                            >
                              <div>
                                <p className="text-white font-medium">
                                  {loan.weapon_names?.join(', ') || 'Ukjent våpen'}
                                </p>
                                <p className="text-gray-400 text-sm">
                                  Utlånt til: {loan.borrower_name}
                                </p>
                              </div>
                              <div className="text-right">
                                {loan.current_loan_id ? (
                                  <a
                                    href={`/weapon-loan/${loan.current_loan_id}`}
                                    className="px-3 py-1.5 bg-orange-500/20 text-orange-400 rounded text-xs hover:bg-orange-500/30 transition-colors inline-flex items-center gap-1"
                                  >
                                    Vis erklæring
                                    <ExternalLink className="w-3 h-3" />
                                  </a>
                                ) : (
                                  <button
                                    onClick={() => {
                                      // Navigate to weapons tab and highlight this weapon
                                      setActiveTab('list');
                                      // Find and open edit modal for this weapon
                                      const weapon = weapons.find(w => w.id === loan.weapon_id);
                                      if (weapon) {
                                        handleEditWeapon(weapon);
                                      }
                                    }}
                                    className="px-3 py-1.5 bg-orange-500/20 text-orange-400 rounded text-xs hover:bg-orange-500/30 transition-colors inline-flex items-center gap-1"
                                  >
                                    Vis detaljer
                                    <Edit className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            </div>
                          )
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">
                        Ingen aktive utlån funnet
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Weapons List Tab */}
            <TabsContent value="list" className="mt-6">
              {/* Actions bar */}
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                {/* Search */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Søk etter merke, modell, kaliber eller serienummer..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    data-testid="weapon-search-input"
                  />
                </div>

                {/* Filters */}
                <div className="flex gap-2 flex-wrap">
                  <Select value={caliberFilter} onValueChange={setCaliberFilter}>
                    <SelectTrigger className="w-[140px] bg-gray-800 border-gray-700 text-white">
                      <SelectValue placeholder="Kaliber" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      <SelectItem value="all" className="text-white">Alle kalibre</SelectItem>
                      {uniqueCalibers.map(cal => (
                        <SelectItem key={cal} value={cal} className="text-white">{cal}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={locationFilter} onValueChange={setLocationFilter}>
                    <SelectTrigger className="w-[160px] bg-gray-800 border-gray-700 text-white">
                      <SelectValue placeholder="Lokasjon" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      <SelectItem value="all" className="text-white">Alle lokasjoner</SelectItem>
                      {locations.map(loc => (
                        <SelectItem key={loc.id} value={loc.id} className="text-white">{loc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[140px] bg-gray-800 border-gray-700 text-white">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      <SelectItem value="all" className="text-white">Alle statuser</SelectItem>
                      <SelectItem value="available" className="text-white">Tilgjengelig</SelectItem>
                      <SelectItem value="loaned" className="text-white">Utlånt</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* PDF Download button */}
                <button
                  onClick={handleDownloadWeaponsPDF}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg font-medium hover:bg-gray-600 transition-colors"
                  data-testid="download-weapons-pdf-btn"
                  title="Last ned PDF-liste over alle våpen"
                >
                  <FileText className="w-4 h-4" />
                  <span className="hidden sm:inline">PDF</span>
                </button>

                {/* Import from Excel button */}
                <button
                  onClick={openImportModal}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-500 transition-colors"
                  data-testid="import-weapons-btn"
                  title="Importer flere våpen fra Excel"
                >
                  <Upload className="w-4 h-4" />
                  <span className="hidden sm:inline">Importer fra Excel</span>
                </button>

                {/* Add button */}
                <button
                  onClick={handleAddWeapon}
                  className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors"
                  data-testid="add-weapon-btn"
                >
                  <Plus className="w-4 h-4" />
                  {t.weapon.addNew}
                </button>
              </div>

              {/* Weapons table */}
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 text-yellow-500 animate-spin" />
                </div>
              ) : filteredWeapons.length === 0 ? (
                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="py-12 text-center">
                    <Shield className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">Ingen våpen funnet</p>
                    <button
                      onClick={handleAddWeapon}
                      className="mt-4 px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400"
                    >
                      Legg til ditt første våpen
                    </button>
                  </CardContent>
                </Card>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-gray-700">
                  <table className="w-full" data-testid="weapons-table">
                    <thead className="bg-gray-800">
                      <tr>
                        <th
                          className="px-4 py-3 text-left text-sm font-medium text-gray-400 cursor-pointer hover:text-white"
                          onClick={() => handleSort('brand')}
                        >
                          <div className="flex items-center gap-1">
                            {t.weapon.brand}/{t.weapon.model}
                            <ArrowUpDown className="w-3 h-3" />
                          </div>
                        </th>
                        <th
                          className="px-4 py-3 text-left text-sm font-medium text-gray-400 cursor-pointer hover:text-white"
                          onClick={() => handleSort('caliber')}
                        >
                          <div className="flex items-center gap-1">
                            {t.weapon.caliber}
                            <ArrowUpDown className="w-3 h-3" />
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                          {t.weapon.serialNumber}
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                          {t.weapon.location}
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                          {t.weapon.status}
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">
                          {t.maintenance.lastCleaned}
                        </th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-400">
                          {t.common.actions}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {filteredWeapons.map((weapon) => (
                        <tr
                          key={weapon.id}
                          className="bg-gray-800/50 hover:bg-gray-700/50 transition-colors cursor-pointer"
                          data-testid={`weapon-row-${weapon.id}`}
                          onDoubleClick={() => handleEditWeapon(weapon)}
                          title="Dobbeltklikk for å redigere"
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gray-700 rounded-lg flex items-center justify-center">
                                <Shield className="w-5 h-5 text-yellow-500" />
                              </div>
                              <div>
                                <p className="text-white font-medium">{weapon.brand}</p>
                                <p className="text-gray-400 text-sm">{weapon.model}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 bg-gray-700 rounded text-gray-300 text-sm">
                              {weapon.caliber}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-400 font-mono text-sm">
                            {weapon.serial_number}
                          </td>
                          <td className="px-4 py-3 text-gray-400">
                            {getLocationName(weapon.location_id, weapon)}
                          </td>
                          <td className="px-4 py-3">
                            {(() => {
                              const status = weapon.status || (weapon.is_loaned ? 'loaned' : 'available');
                              const statusConfig = {
                                'available': { icon: CheckCircle, label: 'Tilgjengelig', colorClass: 'text-green-400 bg-green-500/20' },
                                'loaned': { icon: User, label: 'Utlånt', colorClass: 'text-orange-400 bg-orange-500/20', isLink: true },
                                'maintenance': { icon: Wrench, label: 'Under vedlikehold', colorClass: 'text-blue-400 bg-blue-500/20' },
                                'repair': { icon: AlertTriangle, label: 'Til reparasjon', colorClass: 'text-red-400 bg-red-500/20' },
                                'decommissioned': { icon: X, label: 'Utgått/Kassert', colorClass: 'text-gray-400 bg-gray-500/20' },
                                'reserved': { icon: Clock, label: 'Reservert', colorClass: 'text-purple-400 bg-purple-500/20' }
                              };
                              
                              const config = statusConfig[status] || { icon: Shield, label: status, colorClass: 'text-yellow-400 bg-yellow-500/20' };
                              const IconComponent = config.icon;
                              
                              if (status === 'loaned' && weapon.current_loan_id) {
                                return (
                                  <a
                                    href={`/weapon-loan/${weapon.current_loan_id}`}
                                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium hover:opacity-80 transition-colors cursor-pointer ${config.colorClass}`}
                                    title="Klikk for å se utlånserklæring"
                                    data-testid={`loan-tag-${weapon.id}`}
                                  >
                                    <IconComponent className="w-4 h-4" />
                                    {config.label}
                                  </a>
                                );
                              }
                              
                              return (
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${config.colorClass}`}>
                                  <IconComponent className="w-4 h-4" />
                                  {config.label}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-3 text-gray-400 text-sm">
                            {weapon.last_cleaned ? (
                              <span className="flex items-center gap-2">
                                <span
                                  className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                                  style={{ backgroundColor: getCleaningColor(weapon.last_cleaned) }}
                                  data-testid={`cleaning-dot-${weapon.id}`}
                                />
                                {new Date(weapon.last_cleaned).toLocaleDateString('nb-NO')}
                              </span>
                            ) : (
                              <span className="flex items-center gap-2">
                                <span
                                  className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                                  style={{ backgroundColor: '#ef4444' }}
                                />
                                <span className="text-gray-500">Aldri</span>
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              {/* Quick loan / return actions */}
                              {(weapon.status === 'available' || (!weapon.is_loaned && !weapon.status)) && (
                                <button
                                  onClick={() => openQuickLoan(weapon)}
                                  className="p-2 text-gray-400 hover:text-orange-400 hover:bg-gray-700 rounded-lg transition-colors"
                                  title="Lån ut våpen"
                                  data-testid={`quick-loan-${weapon.id}`}
                                >
                                  <UserPlus className="w-4 h-4" />
                                </button>
                              )}
                              {(weapon.is_loaned || weapon.status === 'loaned') && (
                                <button
                                  onClick={() => openQuickReturn(weapon)}
                                  className="p-2 text-gray-400 hover:text-green-400 hover:bg-gray-700 rounded-lg transition-colors"
                                  title={`Marker som tilbakelevert${weapon.loaned_to ? ` (fra ${weapon.loaned_to})` : ''}`}
                                  data-testid={`quick-return-${weapon.id}`}
                                >
                                  <RotateCcw className="w-4 h-4" />
                                </button>
                              )}
                              <button
                                onClick={() => handleViewWeapon(weapon)}
                                className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                                title="Vis detaljer"
                                data-testid={`view-weapon-${weapon.id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleEditWeapon(weapon)}
                                className="p-2 text-gray-400 hover:text-blue-400 hover:bg-gray-700 rounded-lg transition-colors"
                                title="Rediger"
                                data-testid={`edit-weapon-${weapon.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDownloadQR(weapon)}
                                className="p-2 text-gray-400 hover:text-yellow-400 hover:bg-gray-700 rounded-lg transition-colors"
                                title="Last ned QR-kode"
                                data-testid={`download-qr-${weapon.id}`}
                              >
                                <QrCode className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedWeapon(weapon);
                                  setShowDeleteConfirmModal(true);
                                }}
                                className="p-2 text-gray-400 hover:text-red-400 hover:bg-gray-700 rounded-lg transition-colors"
                                title="Slett"
                                data-testid={`delete-weapon-${weapon.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Summary */}
              {filteredWeapons.length > 0 && (
                <div className="mt-4 text-sm text-gray-500">
                  Viser {filteredWeapons.length} av {weapons.length} våpen
                </div>
              )}
            </TabsContent>

            {/* History Tab */}
            <TabsContent value="history" className="mt-6">
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <CardTitle className="text-white flex items-center gap-2">
                        <History className="w-5 h-5 text-yellow-500" />
                        Vedlikeholdshistorikk
                      </CardTitle>
                      <CardDescription className="text-gray-400">
                        Oversikt over pussing, vedlikehold og utlån av klubbens våpen
                      </CardDescription>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Select value={historyFilter} onValueChange={(val) => { setHistoryFilter(val); setHistoryPage(1); }}>
                        <SelectTrigger className="w-[140px] bg-gray-700 border-gray-600 text-white">
                          <SelectValue placeholder="Filter" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700">
                          <SelectItem value="all" className="text-white">Alle hendelser</SelectItem>
                          <SelectItem value="cleaning" className="text-white">Pussing</SelectItem>
                          <SelectItem value="maintenance" className="text-white">Vedlikehold</SelectItem>
                          <SelectItem value="loan" className="text-white">Utlån</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={String(historyLimit)} onValueChange={(val) => { setHistoryLimit(val === 'all' ? 'all' : parseInt(val)); setHistoryPage(1); }}>
                        <SelectTrigger className="w-[100px] bg-gray-700 border-gray-600 text-white">
                          <SelectValue placeholder="Antall" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700">
                          <SelectItem value="25" className="text-white">25</SelectItem>
                          <SelectItem value="50" className="text-white">50</SelectItem>
                          <SelectItem value="100" className="text-white">100</SelectItem>
                          <SelectItem value="all" className="text-white">Alle</SelectItem>
                        </SelectContent>
                      </Select>
                      <button
                        onClick={loadWeaponHistory}
                        className="p-2 bg-gray-700 rounded-lg text-gray-300 hover:bg-gray-600"
                        title="Oppdater"
                      >
                        <RefreshCw className={`w-4 h-4 ${historyLoading ? 'animate-spin' : ''}`} />
                      </button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {historyLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <RefreshCw className="w-8 h-8 text-yellow-500 animate-spin" />
                    </div>
                  ) : weaponHistory.length === 0 ? (
                    <div className="text-center py-12">
                      <History className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-400">Ingen historikk funnet</p>
                      <p className="text-gray-500 text-sm mt-2">
                        Bruk QR-skanneren for å registrere pussing eller vedlikehold
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-3">
                        {(() => {
                          const itemsPerPage = historyLimit === 'all' ? weaponHistory.length : historyLimit;
                          const startIndex = (historyPage - 1) * itemsPerPage;
                          const endIndex = historyLimit === 'all' ? weaponHistory.length : startIndex + itemsPerPage;
                          const paginatedHistory = weaponHistory.slice(startIndex, endIndex);
                          
                          return paginatedHistory.map((item) => (
                            <div
                              key={item.id}
                              className="flex items-start gap-4 p-4 bg-gray-700/50 rounded-lg hover:bg-gray-700 transition-colors"
                            >
                              {/* Icon */}
                              <div className={`p-2 rounded-full ${
                                item.type === 'cleaning' ? 'bg-yellow-500/20 text-yellow-500' :
                                item.type === 'maintenance' ? 'bg-blue-500/20 text-blue-500' :
                                'bg-purple-500/20 text-purple-500'
                              }`}>
                                {item.type === 'cleaning' && <Sparkles className="w-5 h-5" />}
                                {item.type === 'maintenance' && <Wrench className="w-5 h-5" />}
                                {item.type === 'loan' && <User className="w-5 h-5" />}
                              </div>
                              
                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    item.type === 'cleaning' ? 'bg-yellow-500/20 text-yellow-400' :
                                    item.type === 'maintenance' ? 'bg-blue-500/20 text-blue-400' :
                                    'bg-purple-500/20 text-purple-400'
                                  }`}>
                                    {item.type === 'cleaning' ? 'Pussing' :
                                     item.type === 'maintenance' ? 'Vedlikehold' : 'Utlån'}
                                  </span>
                                  <span className="text-gray-500 text-sm">
                                    {item.date ? new Date(item.date).toLocaleDateString('nb-NO', {
                                      day: 'numeric',
                                      month: 'short',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    }) : ''}
                                  </span>
                                </div>
                                
                                <p className="text-white font-medium">
                                  {item.weapon_name}
                                  {item.weapon_caliber && (
                                    <span className="text-gray-400 font-normal ml-2">({item.weapon_caliber})</span>
                                  )}
                                </p>
                                
                                <p className="text-gray-400 text-sm">
                                  {item.type === 'cleaning' && (
                                    <>Pusset av {item.user_name}{item.notes && ` - ${item.notes}`}</>
                                  )}
                                  {item.type === 'maintenance' && (
                                    <>
                                      {item.description} - {item.user_name}
                                      {item.cost && <span className="text-blue-400 ml-2">({item.cost} kr)</span>}
                                    </>
                                  )}
                                  {item.type === 'loan' && (
                                    <>
                                      Utlånt til {item.borrower_name} av {item.user_name}
                                      {item.returned_at && <span className="text-green-400 ml-2">(Returnert)</span>}
                                    </>
                                  )}
                                </p>
                              </div>
                              
                              {/* Edit/Delete buttons for cleaning and maintenance (not loans) */}
                              {item.type !== 'loan' && (
                                <div className="flex items-center gap-1 ml-2">
                                  <button
                                    onClick={() => handleEditHistoryItem(item)}
                                    className="p-1.5 text-gray-400 hover:text-blue-400 hover:bg-gray-600 rounded transition-colors"
                                    title="Rediger"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteHistoryItem(item)}
                                    className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-gray-600 rounded transition-colors"
                                    title="Slett"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                            </div>
                          ));
                        })()}
                      </div>
                      
                      {/* Pagination */}
                      {historyLimit !== 'all' && weaponHistory.length > historyLimit && (
                        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-700">
                          <span className="text-sm text-gray-400">
                            Viser {Math.min((historyPage - 1) * historyLimit + 1, weaponHistory.length)}-{Math.min(historyPage * historyLimit, weaponHistory.length)} av {weaponHistory.length} hendelser
                          </span>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setHistoryPage(p => Math.max(1, p - 1))}
                              disabled={historyPage === 1}
                              className="px-3 py-1 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Forrige
                            </button>
                            <span className="px-3 py-1 text-gray-400">
                              Side {historyPage} av {Math.ceil(weaponHistory.length / historyLimit)}
                            </span>
                            <button
                              onClick={() => setHistoryPage(p => Math.min(Math.ceil(weaponHistory.length / historyLimit), p + 1))}
                              disabled={historyPage >= Math.ceil(weaponHistory.length / historyLimit)}
                              className="px-3 py-1 bg-gray-700 text-gray-300 rounded hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Neste
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {/* Show total count when showing all */}
                      {historyLimit === 'all' && (
                        <div className="mt-4 text-sm text-gray-500">
                          Viser alle {weaponHistory.length} hendelser
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Audit Log Tab - Endringslogg */}
            <TabsContent value="audit" className="mt-6">
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <History className="w-5 h-5" />
                    Endringslogg for våpen
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {auditLogs.length === 0 ? (
                    <div className="text-center py-8 text-gray-400">
                      Ingen endringer registrert ennå
                    </div>
                  ) : (
                    <div className="overflow-x-auto -mx-2 md:mx-0">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-900/60">
                          <tr>
                            <th className="px-2 md:px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Dato</th>
                            <th className="px-2 md:px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Admin</th>
                            <th className="px-2 md:px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Handling</th>
                            <th className="px-2 md:px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Våpen</th>
                            <th className="px-2 md:px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Detaljer</th>
                            <th className="px-2 md:px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Handling</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700">
                          {auditLogs.map((log) => (
                            <tr key={log.id} className="hover:bg-gray-700/40" data-testid={`audit-row-${log.id}`}>
                              <td className="px-2 md:px-4 py-3 text-gray-300 whitespace-nowrap">
                                {log.changed_at ? new Date(log.changed_at).toLocaleString('no-NO', {
                                  year: 'numeric', month: '2-digit', day: '2-digit',
                                  hour: '2-digit', minute: '2-digit'
                                }) : '-'}
                              </td>
                              <td className="px-2 md:px-4 py-3 text-white whitespace-nowrap">{log.changed_by_name || 'Ukjent'}</td>
                              <td className="px-2 md:px-4 py-3 whitespace-nowrap">
                                <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                                  log.action === 'created' ? 'bg-green-500/20 text-green-400' :
                                  log.action === 'updated' ? 'bg-blue-500/20 text-blue-400' :
                                  log.action === 'deleted' ? 'bg-red-500/20 text-red-400' :
                                  log.action === 'restored' ? 'bg-purple-500/20 text-purple-400' :
                                  'bg-gray-500/20 text-gray-400'
                                }`}>
                                  {log.action === 'created' ? 'Opprettet' :
                                   log.action === 'updated' ? 'Endret' :
                                   log.action === 'deleted' ? 'Slettet' :
                                   log.action === 'restored' ? 'Gjenopprettet' : log.action}
                                </span>
                              </td>
                              <td className="px-2 md:px-4 py-3 text-gray-300">
                                <div className="font-medium">{log.weapon_label}</div>
                                {log.serial_number && (
                                  <div className="text-xs text-gray-500">SN: {log.serial_number}</div>
                                )}
                              </td>
                              <td className="px-2 md:px-4 py-3 text-gray-300 min-w-[220px]">
                                {log.changes && Object.keys(log.changes).length > 0 ? (
                                  <div className="space-y-1">
                                    {Object.entries(log.changes).map(([field, val]) => (
                                      <div key={field} className="text-xs">
                                        <span className="font-semibold text-gray-400">{field}: </span>
                                        <span className="line-through text-red-400 mr-1">{val.old ?? '-'}</span>
                                        <span className="text-green-400">→ {val.new ?? '-'}</span>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-500">{log.description || '-'}</span>
                                )}
                              </td>
                              <td className="px-2 md:px-4 py-3 text-right whitespace-nowrap">
                                {log.action === 'deleted' && log.can_restore && (
                                  <button
                                    onClick={async () => {
                                      try {
                                        const token = localStorage.getItem('token');
                                        await axios.post(
                                          `${API_BASE}/api/club-operations/weapons/${log.weapon_id}/restore`,
                                          {},
                                          { headers: { Authorization: `Bearer ${token}` } }
                                        );
                                        toast.success('Våpen gjenopprettet');
                                        const r = await axios.get(`${API_BASE}/api/club-operations/weapons/audit-logs`, {
                                          headers: { Authorization: `Bearer ${token}` }
                                        });
                                        setAuditLogs(r.data?.logs || []);
                                        loadWeapons();
                                      } catch (e) {
                                        toast.error(e?.response?.data?.detail || 'Kunne ikke gjenopprette');
                                      }
                                    }}
                                    className="px-3 py-1 bg-purple-600 hover:bg-purple-500 text-white text-xs rounded font-medium"
                                    data-testid={`restore-weapon-${log.weapon_id}`}
                                  >
                                    Gjenopprett
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Endringslogg (Audit) - permanent bottom section (matches placement of other endringslogger) */}
          <Card className="bg-gray-800 border-gray-700 mt-8" data-testid="weapons-audit-section">
            <CardHeader
              className="cursor-pointer select-none hover:bg-gray-700/40 transition-colors rounded-t-lg"
              onClick={() => setAuditExpanded(v => !v)}
              data-testid="weapons-audit-toggle"
            >
              <CardTitle className="text-white flex items-center justify-between gap-2">
                <span className="flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Endringslogg for våpen
                  {auditLogs.length > 0 && (
                    <span className="text-xs font-normal text-gray-400 ml-2">
                      ({auditLogs.length})
                    </span>
                  )}
                </span>
                {auditExpanded ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </CardTitle>
            </CardHeader>
            {auditExpanded && (
            <CardContent>
              {auditLogs.length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  Ingen endringer registrert ennå
                </div>
              ) : (
                <div className="overflow-x-auto -mx-2 md:mx-0">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-900/60">
                      <tr>
                        <th className="px-2 md:px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Dato</th>
                        <th className="px-2 md:px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Admin</th>
                        <th className="px-2 md:px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Handling</th>
                        <th className="px-2 md:px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Våpen</th>
                        <th className="px-2 md:px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Detaljer</th>
                        <th className="px-2 md:px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase">Handling</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {auditLogs.map((log) => (
                        <tr key={log.id} className="hover:bg-gray-700/40" data-testid={`audit-row-${log.id}`}>
                          <td className="px-2 md:px-4 py-3 text-gray-300 whitespace-nowrap">
                            {log.changed_at ? new Date(log.changed_at).toLocaleString('no-NO', {
                              year: 'numeric', month: '2-digit', day: '2-digit',
                              hour: '2-digit', minute: '2-digit'
                            }) : '-'}
                          </td>
                          <td className="px-2 md:px-4 py-3 text-white whitespace-nowrap">{log.changed_by_name || 'Ukjent'}</td>
                          <td className="px-2 md:px-4 py-3 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                              log.action === 'created' ? 'bg-green-500/20 text-green-400' :
                              log.action === 'updated' ? 'bg-blue-500/20 text-blue-400' :
                              log.action === 'deleted' ? 'bg-red-500/20 text-red-400' :
                              log.action === 'restored' ? 'bg-purple-500/20 text-purple-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>
                              {log.action === 'created' ? 'Opprettet' :
                               log.action === 'updated' ? 'Endret' :
                               log.action === 'deleted' ? 'Slettet' :
                               log.action === 'restored' ? 'Gjenopprettet' : log.action}
                            </span>
                          </td>
                          <td className="px-2 md:px-4 py-3 text-gray-300">
                            <div className="font-medium">{log.weapon_label}</div>
                            {log.serial_number && (
                              <div className="text-xs text-gray-500">SN: {log.serial_number}</div>
                            )}
                          </td>
                          <td className="px-2 md:px-4 py-3 text-gray-300 min-w-[220px]">
                            {log.changes && Object.keys(log.changes).length > 0 ? (
                              <div className="space-y-1">
                                {Object.entries(log.changes).map(([field, val]) => (
                                  <div key={field} className="text-xs">
                                    <span className="font-semibold text-gray-400">{field}: </span>
                                    <span className="line-through text-red-400 mr-1">{val.old ?? '-'}</span>
                                    <span className="text-green-400">→ {val.new ?? '-'}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-500">{log.description || '-'}</span>
                            )}
                          </td>
                          <td className="px-2 md:px-4 py-3 text-right whitespace-nowrap">
                            {log.action === 'deleted' && log.can_restore && (
                              <button
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('token');
                                    await axios.post(
                                      `${API_BASE}/api/club-operations/weapons/${log.weapon_id}/restore`,
                                      {},
                                      { headers: { Authorization: `Bearer ${token}` } }
                                    );
                                    toast.success('Våpen gjenopprettet');
                                    const r = await axios.get(`${API_BASE}/api/club-operations/weapons/audit-logs`, {
                                      headers: { Authorization: `Bearer ${token}` }
                                    });
                                    setAuditLogs(r.data?.logs || []);
                                    loadWeapons();
                                  } catch (e) {
                                    toast.error(e?.response?.data?.detail || 'Kunne ikke gjenopprette');
                                  }
                                }}
                                className="px-3 py-1 bg-purple-600 hover:bg-purple-500 text-white text-xs rounded font-medium"
                                data-testid={`restore-weapon-${log.weapon_id}`}
                              >
                                Gjenopprett
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
            )}
          </Card>
        </div>
      )}

      {/* Ammunition Module */}
      {activeModule === 'ammo' && (
        <AmmoManagement />
      )}

      {/* Range Management Module */}
      {activeModule === 'range' && (
        <RangeManagement />
      )}

      {activeModule === 'keys' && (
        <KeyAdmin toast={toast} />
      )}

      {/* Edit History Item Modal */}
      <Dialog open={!!editingHistoryItem} onOpenChange={(open) => !open && setEditingHistoryItem(null)}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              {editingHistoryItem?.type === 'cleaning' ? (
                <>
                  <Sparkles className="w-5 h-5 text-yellow-500" />
                  Rediger pussing
                </>
              ) : (
                <>
                  <Wrench className="w-5 h-5 text-blue-500" />
                  Rediger vedlikehold
                </>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {editingHistoryItem?.type === 'cleaning' ? (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Notater
                </label>
                <textarea
                  value={editHistoryForm.notes}
                  onChange={(e) => setEditHistoryForm({ ...editHistoryForm, notes: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 min-h-[100px]"
                  placeholder="Legg til notater om pussingen..."
                />
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Beskrivelse
                  </label>
                  <textarea
                    value={editHistoryForm.description}
                    onChange={(e) => setEditHistoryForm({ ...editHistoryForm, description: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[100px]"
                    placeholder="Beskrivelse av vedlikeholdet..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Kostnad (kr)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={editHistoryForm.cost}
                    onChange={(e) => setEditHistoryForm({ ...editHistoryForm, cost: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="0.00"
                  />
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={() => setEditingHistoryItem(null)}
              className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
            >
              Avbryt
            </button>
            <button
              type="button"
              onClick={handleSaveHistoryItem}
              className={`px-4 py-2 text-white rounded-lg transition-colors ${
                editingHistoryItem?.type === 'cleaning' 
                  ? 'bg-yellow-500 hover:bg-yellow-600' 
                  : 'bg-blue-500 hover:bg-blue-600'
              }`}
            >
              Lagre endringer
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Weapon Modal */}
      <Dialog open={showAddWeaponModal} onOpenChange={setShowAddWeaponModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Shield className="w-5 h-5 text-yellow-500" />
              {isEditing ? t.weapon.edit : t.weapon.addNew}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {isEditing ? 'Oppdater informasjon om våpenet' : 'Fyll ut informasjon om det nye våpenet'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmitWeapon} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {t.weapon.brand} *
                </label>
                <input
                  type="text"
                  required
                  value={weaponFormData.brand}
                  onChange={(e) => setWeaponFormData({ ...weaponFormData, brand: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="F.eks. Sauer"
                  data-testid="weapon-brand-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {t.weapon.model} *
                </label>
                <input
                  type="text"
                  required
                  value={weaponFormData.model}
                  onChange={(e) => setWeaponFormData({ ...weaponFormData, model: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="F.eks. 200 STR"
                  data-testid="weapon-model-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {t.weapon.caliber} *
                </label>
                <input
                  type="text"
                  required
                  value={weaponFormData.caliber}
                  onChange={(e) => setWeaponFormData({ ...weaponFormData, caliber: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="F.eks. 6.5x55"
                  data-testid="weapon-caliber-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {t.weapon.serialNumber} *
                </label>
                <input
                  type="text"
                  required
                  value={weaponFormData.serial_number}
                  onChange={(e) => setWeaponFormData({ ...weaponFormData, serial_number: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="F.eks. ABC123456"
                  data-testid="weapon-serial-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {t.weapon.magazineCount}
                </label>
                <input
                  type="number"
                  min="0"
                  value={weaponFormData.magazine_count}
                  onChange={(e) => setWeaponFormData({ ...weaponFormData, magazine_count: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  data-testid="weapon-magazine-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  {t.weapon.location}
                </label>
                <select
                  value={showCustomLocation ? 'custom' : weaponFormData.location_id}
                  onChange={(e) => {
                    if (e.target.value === 'custom') {
                      setShowCustomLocation(true);
                      setWeaponFormData({ ...weaponFormData, location_id: 'custom' });
                    } else {
                      setShowCustomLocation(false);
                      setCustomLocationName('');
                      setWeaponFormData({ ...weaponFormData, location_id: e.target.value });
                    }
                  }}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  data-testid="weapon-location-select"
                >
                  <option value="">Velg lokasjon</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                  <option value="custom">+ Egendefinert lokasjon</option>
                </select>
              </div>
            </div>

            {/* Custom location input */}
            {showCustomLocation && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Egendefinert lokasjon *
                </label>
                <input
                  type="text"
                  value={customLocationName}
                  onChange={(e) => setCustomLocationName(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="F.eks. Våpenskap 3, Hylle B"
                  data-testid="weapon-custom-location-input"
                />
              </div>
            )}

            {/* Status Section */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {t.weapon.status}
              </label>
              <select
                value={weaponFormData.status}
                onChange={(e) => setWeaponFormData({ ...weaponFormData, status: e.target.value, custom_status: e.target.value === 'custom' ? weaponFormData.custom_status : '' })}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                data-testid="weapon-status-select"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            {/* Custom status input */}
            {weaponFormData.status === 'custom' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Egendefinert status *
                </label>
                <input
                  type="text"
                  value={weaponFormData.custom_status}
                  onChange={(e) => setWeaponFormData({ ...weaponFormData, custom_status: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="F.eks. Venter på deler, I karantene"
                  data-testid="weapon-custom-status-input"
                />
              </div>
            )}

            {/* Image Upload Section */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                {t.weapon.images}
              </label>
              
              {/* Compression progress */}
              {compressionProgress !== null && (
                <div className="mb-3 p-3 bg-gray-700 rounded-lg">
                  <div className="flex justify-between text-sm text-gray-400 mb-1">
                    <span>Komprimerer bilde...</span>
                    <span>{compressionProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-600 rounded-full h-2">
                    <div 
                      className="bg-yellow-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${compressionProgress}%` }}
                    />
                  </div>
                </div>
              )}
              
              {/* Image thumbnails grid */}
              {weaponImages.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {weaponImages.map((img, index) => {
                    const imgUrl = typeof img === 'string' ? img : img.dataUrl;
                    return (
                      <div key={index} className="relative group aspect-square">
                        <img
                          src={imgUrl}
                          alt={`Bilde ${index + 1}`}
                          className="w-full h-full object-cover rounded-lg border border-gray-600"
                        />
                        {/* Action overlay */}
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-1">
                          <button
                            type="button"
                            onClick={() => setPreviewImage(imgUrl)}
                            className="p-1.5 bg-blue-500 text-white rounded hover:bg-blue-600"
                            title="Forhåndsvis"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <a
                            href={imgUrl}
                            download={`våpen-bilde-${index + 1}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 bg-green-500 text-white rounded hover:bg-green-600"
                            title="Last ned"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                          <button
                            type="button"
                            onClick={() => setShowDeleteImageConfirm({ url: img, index })}
                            className="p-1.5 bg-red-500 text-white rounded hover:bg-red-600"
                            title="Slett"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Upload button */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => handleImageUpload(Array.from(e.target.files))}
                data-testid="weapon-image-upload"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingImage}
                className="flex items-center gap-2 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-300 hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                {uploadingImage ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Laster opp...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Last opp bilder
                  </>
                )}
              </button>
              <p className="text-xs text-gray-500 mt-1">JPG, PNG • Auto-komprimering til 1200px</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Notater
              </label>
              <textarea
                value={weaponFormData.notes}
                onChange={(e) => setWeaponFormData({ ...weaponFormData, notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 resize-none"
                placeholder="Valgfrie notater om våpenet..."
                data-testid="weapon-notes-input"
              />
            </div>

            <DialogFooter className="mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowAddWeaponModal(false);
                  resetForm();
                }}
                className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
              >
                {t.common.cancel}
              </button>
              <button
                type="submit"
                disabled={formLoading}
                className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="submit-weapon-btn"
              >
                {formLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Lagrer...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    {isEditing ? 'Oppdater' : t.common.save}
                  </>
                )}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Weapon Detail Modal */}
      <Dialog open={showWeaponDetailModal} onOpenChange={setShowWeaponDetailModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Shield className="w-5 h-5 text-yellow-500" />
              {selectedWeapon?.brand} {selectedWeapon?.model}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Detaljert informasjon om våpenet
            </DialogDescription>
          </DialogHeader>

          {selectedWeapon && (
            <div className="space-y-6 mt-4">
              {/* Basic info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-400">{t.weapon.caliber}</p>
                  <p className="text-white font-medium">{selectedWeapon.caliber}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">{t.weapon.serialNumber}</p>
                  <p className="text-white font-mono">{selectedWeapon.serial_number}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">{t.weapon.magazineCount}</p>
                  <p className="text-white">{selectedWeapon.magazine_count || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">{t.weapon.location}</p>
                  <p className="text-white">{getLocationName(selectedWeapon.location_id, selectedWeapon)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-400">{t.weapon.status}</p>
                  {selectedWeapon.is_loaned ? (
                    <span className="text-orange-400 flex items-center gap-1">
                      <User className="w-4 h-4" />
                      Utlånt
                    </span>
                  ) : (
                    <span className="text-green-400 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" />
                      Tilgjengelig
                    </span>
                  )}
                </div>
                <div>
                  <p className="text-sm text-gray-400">{t.maintenance.lastCleaned}</p>
                  <p className="text-white">
                    {selectedWeapon.last_cleaned
                      ? new Date(selectedWeapon.last_cleaned).toLocaleDateString('nb-NO')
                      : 'Aldri'}
                  </p>
                </div>
              </div>

              {/* Notes */}
              {selectedWeapon.notes && (
                <div>
                  <p className="text-sm text-gray-400 mb-1">Notater</p>
                  <p className="text-gray-300 bg-gray-700/50 p-3 rounded-lg">
                    {selectedWeapon.notes}
                  </p>
                </div>
              )}

              {/* QR Code preview */}
              <div className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg">
                <div className="flex items-center gap-3">
                  <QrCode className="w-8 h-8 text-yellow-500" />
                  <div>
                    <p className="text-white font-medium">QR-kode</p>
                    <p className="text-sm text-gray-400">Last ned for intern skanning</p>
                  </div>
                </div>
                <button
                  onClick={() => handleDownloadQR(selectedWeapon)}
                  className="px-4 py-2 bg-yellow-500 text-gray-900 rounded-lg font-medium hover:bg-yellow-400 transition-colors flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Last ned
                </button>
              </div>

              {/* Cleaning count */}
              <div className="flex items-center gap-4 p-4 bg-gray-700/50 rounded-lg">
                <Sparkles className="w-8 h-8 text-green-400" />
                <div>
                  <p className="text-white font-medium">Pusset {selectedWeapon.cleaning_count || 0} ganger</p>
                  <p className="text-sm text-gray-400">
                    {selectedWeapon.last_cleaned
                      ? `Sist: ${new Date(selectedWeapon.last_cleaned).toLocaleDateString('nb-NO')}`
                      : 'Ikke pusset ennå'}
                  </p>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="mt-6">
            <button
              onClick={() => setShowWeaponDetailModal(false)}
              className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
            >
              {t.common.close}
            </button>
            <button
              onClick={() => {
                setShowWeaponDetailModal(false);
                handleEditWeapon(selectedWeapon);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors flex items-center gap-2"
            >
              <Edit className="w-4 h-4" />
              {t.common.edit}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteConfirmModal} onOpenChange={setShowDeleteConfirmModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Slett våpen
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Er du sikker på at du vil slette dette våpenet?
            </DialogDescription>
          </DialogHeader>

          {selectedWeapon && (
            <div className="my-4 p-4 bg-gray-700/50 rounded-lg">
              <p className="text-white font-medium">
                {selectedWeapon.brand} {selectedWeapon.model}
              </p>
              <p className="text-sm text-gray-400">
                {selectedWeapon.caliber} • {selectedWeapon.serial_number}
              </p>
            </div>
          )}

          <DialogFooter>
            <button
              onClick={() => {
                setShowDeleteConfirmModal(false);
                setSelectedWeapon(null);
              }}
              className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
            >
              {t.common.cancel}
            </button>
            <button
              onClick={handleDeleteWeapon}
              className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-500 transition-colors flex items-center gap-2"
              data-testid="confirm-delete-btn"
            >
              <Trash2 className="w-4 h-4" />
              Slett
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Preview Modal - Higher z-index to show above dialogs */}
      {previewImage && (
        <div 
          className="fixed inset-0 bg-black/90 flex items-center justify-center"
          style={{ zIndex: 9999 }}
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] mx-4">
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 text-2xl"
            >
              <X className="w-8 h-8" />
            </button>
            <img
              src={previewImage}
              alt="Forhåndsvisning"
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
          </div>
        </div>
      )}

      {/* Delete Image Confirmation Modal */}
      <Dialog open={showDeleteImageConfirm !== null} onOpenChange={() => setShowDeleteImageConfirm(null)}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Slett bilde
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Er du sikker på at du vil slette dette bildet?
            </DialogDescription>
          </DialogHeader>

          <div className="my-4 p-4 bg-red-900/20 border border-red-800 rounded-lg">
            <p className="text-red-300 text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Denne handlingen kan ikke angres. Bildet vil bli permanent slettet.
            </p>
          </div>

          <DialogFooter>
            <button
              onClick={() => setShowDeleteImageConfirm(null)}
              className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Avbryt
            </button>
            <button
              onClick={() => {
                if (showDeleteImageConfirm) {
                  handleDeleteImage(showDeleteImageConfirm.url, showDeleteImageConfirm.index);
                }
              }}
              className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-500 transition-colors flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Slett bilde
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Loan Modal */}
      <Dialog open={showQuickLoanModal} onOpenChange={(o) => { if (!o) { setShowQuickLoanModal(false); setQuickLoanWeapon(null); } }}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-orange-400" />
              Lån ut våpen
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {quickLoanWeapon ? `${quickLoanWeapon.brand} ${quickLoanWeapon.model} — ${quickLoanWeapon.caliber} — ${quickLoanWeapon.serial_number}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Låner (medlem)</label>
              <select
                value={quickLoanForm.borrower_id}
                onChange={(e) => setQuickLoanForm(prev => ({ ...prev, borrower_id: e.target.value, borrower_name: '' }))}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                data-testid="quick-loan-borrower-select"
              >
                <option value="">Velg medlem eller skriv navn under...</option>
                {[...orgMembers].sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '', 'nb')).map(m => (
                  <option key={m.id} value={m.id}>{m.full_name || m.email}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">…eller skriv navn manuelt <span className="text-gray-500 font-normal">(hvis ikke medlem)</span></label>
              <input
                type="text"
                value={quickLoanForm.borrower_name}
                onChange={(e) => setQuickLoanForm(prev => ({ ...prev, borrower_name: e.target.value, borrower_id: '' }))}
                placeholder="F.eks. Ola Nordmann"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                data-testid="quick-loan-borrower-name"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Forventet innlevering <span className="text-gray-500 font-normal">(valgfritt)</span></label>
              <input
                type="date"
                value={quickLoanForm.expected_return_date}
                onChange={(e) => setQuickLoanForm(prev => ({ ...prev, expected_return_date: e.target.value }))}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                data-testid="quick-loan-return-date"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Notat <span className="text-gray-500 font-normal">(valgfritt – f.eks. formål)</span></label>
              <textarea
                value={quickLoanForm.notes}
                onChange={(e) => setQuickLoanForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                maxLength={1000}
                placeholder="F.eks. Klubbstevne 20. oktober"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm resize-none"
                data-testid="quick-loan-notes"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <button onClick={() => setShowQuickLoanModal(false)} className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 text-sm">Avbryt</button>
            <button
              onClick={submitQuickLoan}
              disabled={quickActionLoading || (!quickLoanForm.borrower_id && !quickLoanForm.borrower_name.trim())}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-500 text-sm disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
              data-testid="quick-loan-confirm"
            >
              {quickActionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              Registrer utlån
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Weapon Excel Import Modal */}
      <Dialog open={showImportModal} onOpenChange={(o) => { if (!o) { setShowImportModal(false); setImportFile(null); setImportPreview(null); } }}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Upload className="w-5 h-5 text-emerald-400" />
              Importer våpen fra Excel
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Last opp en <code className="text-emerald-400">.xlsx</code>-fil med våpen du vil legge til.
            </DialogDescription>
          </DialogHeader>

          {/* Step 1: File selection + explanation */}
          {!importPreview && (
            <div className="space-y-4 mt-2">
              <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 text-sm text-blue-100">
                <p className="mb-2 font-medium">Excel-filen må inneholde følgende <strong>påkrevde</strong> kolonner:</p>
                <p className="mb-3 text-blue-200">Merke, Modell, Kaliber, Serienummer</p>
                <p className="mb-2 font-medium">Valgfrie kolonner:</p>
                <p className="text-blue-200">Antall magasiner, Lokasjon, Status, Notater</p>
                <p className="mt-3 text-xs text-blue-300">Kolonnenavnene leses uavhengig av store/små bokstaver.</p>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-1">Velg Excel-fil (.xlsx)</label>
                <input
                  type="file"
                  accept=".xlsx"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                  className="w-full text-sm text-gray-300 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-emerald-600 file:text-white hover:file:bg-emerald-500 bg-gray-800 border border-gray-700 rounded-lg p-2"
                  data-testid="import-file-input"
                />
                {importFile && (
                  <p className="mt-1 text-xs text-gray-400">Valgt fil: <span className="text-white">{importFile.name}</span></p>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <button onClick={() => setShowImportModal(false)} className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 text-sm">Avbryt</button>
                <button
                  onClick={handleImportPreview}
                  disabled={!importFile || importLoading}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-500 text-sm disabled:opacity-40 inline-flex items-center gap-2"
                  data-testid="import-preview-btn"
                >
                  {importLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                  Vis forhåndsvisning
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Preview table */}
          {importPreview && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="bg-gray-800 rounded-lg p-3">
                  <div className="text-gray-500 text-xs uppercase">Totalt</div>
                  <div className="text-white font-bold text-lg">{importPreview.total}</div>
                </div>
                <div className="bg-gray-800 rounded-lg p-3">
                  <div className="text-gray-500 text-xs uppercase">Vil importeres</div>
                  <div className="text-green-400 font-bold text-lg">{importPreview.importable}</div>
                </div>
                <div className="bg-gray-800 rounded-lg p-3">
                  <div className="text-gray-500 text-xs uppercase">Duplikat (i DB)</div>
                  <div className="text-yellow-400 font-bold text-lg">{importPreview.duplicates_in_db}</div>
                </div>
                <div className="bg-gray-800 rounded-lg p-3">
                  <div className="text-gray-500 text-xs uppercase">Duplikat / feil i fil</div>
                  <div className="text-red-400 font-bold text-lg">{importPreview.duplicates_in_file + importPreview.invalid}</div>
                </div>
              </div>

              <div className="overflow-x-auto rounded-lg border border-gray-700">
                <table className="w-full" data-testid="import-preview-table">
                  <thead className="bg-gray-800">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-400">Rad</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-400">Merke / Modell</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-400">Kaliber</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-400">Serienummer</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-400">Mag.</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-400">Lokasjon</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-400">Status</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-400">Notater</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-400">Import-status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {importPreview.weapons.map((w, i) => {
                      const isDup = w.is_duplicate_in_db || w.is_duplicate_in_file;
                      const hasErr = w.errors && w.errors.length > 0;
                      const rowClass = hasErr
                        ? 'bg-red-500/10'
                        : isDup
                          ? 'bg-yellow-500/10'
                          : 'bg-gray-900 hover:bg-gray-800/60';
                      return (
                        <tr key={i} className={rowClass} data-testid={`import-preview-row-${i}`}>
                          <td className="px-3 py-2 text-gray-500 text-xs">{w.row_number}</td>
                          <td className="px-3 py-2 text-white text-sm">
                            <div className="font-medium">{w.brand}</div>
                            <div className="text-gray-400 text-xs">{w.model}</div>
                          </td>
                          <td className="px-3 py-2 text-gray-300 text-sm">{w.caliber}</td>
                          <td className="px-3 py-2 text-gray-300 text-sm font-mono">{w.serial_number}</td>
                          <td className="px-3 py-2 text-gray-400 text-sm">{w.magazine_count || 0}</td>
                          <td className="px-3 py-2 text-gray-400 text-sm">{w.location_name || '—'}</td>
                          <td className="px-3 py-2 text-gray-400 text-sm">{w.status || 'available'}</td>
                          <td className="px-3 py-2 text-gray-400 text-xs max-w-[150px] truncate" title={w.notes || ''}>{w.notes || '—'}</td>
                          <td className="px-3 py-2 text-xs">
                            {hasErr ? (
                              <span className="inline-flex items-center gap-1 text-red-400 font-medium">
                                <AlertTriangle className="w-3 h-3" />
                                {w.errors.join(', ')}
                              </span>
                            ) : w.is_duplicate_in_db ? (
                              <span className="inline-flex items-center gap-1 text-yellow-300 font-medium">
                                <AlertTriangle className="w-3 h-3" />
                                Finnes i database
                              </span>
                            ) : w.is_duplicate_in_file ? (
                              <span className="inline-flex items-center gap-1 text-orange-300 font-medium">
                                <AlertTriangle className="w-3 h-3" />
                                Duplikat i fil
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-green-400 font-medium">
                                <CheckCircle className="w-3 h-3" />
                                Klar
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-gray-700">
                <button onClick={() => { setImportPreview(null); setImportFile(null); }} className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 text-sm">Tilbake</button>
                <button
                  onClick={handleImportConfirm}
                  disabled={importConfirming || importPreview.importable === 0}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-500 text-sm disabled:opacity-40 inline-flex items-center gap-2"
                  data-testid="import-confirm-btn"
                >
                  {importConfirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                  Importer {importPreview.importable} våpen
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Quick Return Modal */}
      <Dialog open={showQuickReturnModal} onOpenChange={(o) => { if (!o) { setShowQuickReturnModal(false); setQuickLoanWeapon(null); } }}>        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-green-400" />
              Marker som tilbakelevert
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {quickLoanWeapon ? `${quickLoanWeapon.brand} ${quickLoanWeapon.model} — Utlånt til: ${quickLoanWeapon.loaned_to || 'Ukjent'}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Dato innlevert</label>
              <input
                type="date"
                value={quickReturnForm.returned_date}
                onChange={(e) => setQuickReturnForm(prev => ({ ...prev, returned_date: e.target.value }))}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                data-testid="quick-return-date"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Notat <span className="text-gray-500 font-normal">(valgfritt – f.eks. tilstand)</span></label>
              <textarea
                value={quickReturnForm.notes}
                onChange={(e) => setQuickReturnForm(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                maxLength={1000}
                placeholder="F.eks. Levert i god stand"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm resize-none"
                data-testid="quick-return-notes"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <button onClick={() => setShowQuickReturnModal(false)} className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 text-sm">Avbryt</button>
            <button
              onClick={submitQuickReturn}
              disabled={quickActionLoading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-500 text-sm disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
              data-testid="quick-return-confirm"
            >
              {quickActionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
              Bekreft innlevering
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClubOperations;
